import { ChevronRight } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/frontend/components/ui/badge'
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/frontend/components/ui/card'
import { PrescriptionActionsMenu } from '@/frontend/features/patient/prescription-actions'
import { PrescriptionsDialog } from '@/frontend/features/patient/prescriptions-dialog'
import {
  RefillBadge,
  RefillIntervalBadge,
  RefillStatusBadge,
} from '@/frontend/features/patient/refill-badges'
import { useListPatientTreatments } from '@/frontend/hooks/api/patients/use-list-patient-treatments'
import { groupPrescriptions } from '@/frontend/lib/treatments'

export function PrescriptionsCard({ patientId }: { patientId: string }) {
  const { data, isPending, isError } = useListPatientTreatments(patientId)
  const [open, setOpen] = useState(false)
  const prescriptions = groupPrescriptions(data ?? []).filter(
    (group) => group.latest.isActivePrescription
  )
  const activePrescriptions = prescriptions.map((group) => group.latest)

  return (
    <Card>
      <CardHeader
        className="cursor-pointer"
        role="button"
        tabIndex={0}
        aria-label="View all prescriptions"
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpen(true)
          }
        }}
      >
        <CardTitle>Prescriptions</CardTitle>
        <CardAction>
          <ChevronRight className="size-4 text-muted-foreground" />
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4">
        {isPending ? (
          <p className="text-muted-foreground">Loading prescriptions...</p>
        ) : isError ? (
          <p role="alert">Unable to load prescriptions.</p>
        ) : activePrescriptions.length === 0 ? (
          <p className="text-muted-foreground">No active prescriptions.</p>
        ) : (
          <section>
            <ul className="space-y-2">
              {activePrescriptions.map((rx) => (
                <li key={rx.id} className="border-l-3 border-treatment pl-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium">{rx.productInfo.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {rx.productInfo.quantityLabel}
                        {rx.instructions ? ` - ${rx.instructions}` : ''}
                      </p>
                      {(rx.isExternal || (rx.repeatsAllowed ?? 0) > 0) && (
                        <div className="mt-1 flex flex-wrap items-center gap-1">
                          {rx.isExternal ? (
                            <Badge variant="pending" size="xs">
                              External
                            </Badge>
                          ) : (
                            <>
                              <RefillBadge rx={rx} />
                              <RefillIntervalBadge rx={rx} />
                              <RefillStatusBadge rx={rx} />
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <PrescriptionActionsMenu rx={rx} patientId={patientId} />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </CardContent>
      <PrescriptionsDialog patientId={patientId} open={open} onOpenChange={setOpen} />
    </Card>
  )
}
